import { useCallback, useEffect, useState } from "react";

import { getAllQuoteRevisions } from "../../../api";
import type { RfxQuoteTable } from "../../../types/quote.type";
import type { QuoteRevisionSnapshot } from "../../../types/revision.type";
import { getQuoteDiff } from "../../../../components/QuoteTable/utils";
import { cellDataToLookup, getCellDiff } from "../utils";

export interface UseDraftRevisionsResult {
	draftRevisions: Record<number, QuoteRevisionSnapshot>;
	isQuotesLoading: boolean;
	refreshingQuoteId: number | null;
	setRefreshingQuoteId: (id: number | null) => void;
	quoteDiffKeys: Record<number, Set<string>>;
	fetchDraftRevisions: () => Promise<void>;
	hasDrafts: boolean;
}

/**
 * Owns draft-revision lifecycle for BidderQuoteTab: fetches per-quote drafts
 * (only when invite is in BEING_REVISED), computes diff key sets against the
 * previous submitted revision for read-only highlighting, and exposes a
 * refresh trigger for child modals after save.
 */
export const useDraftRevisions = (
	rfxInviteId: number | undefined,
	listQuotes: RfxQuoteTable[] | undefined,
	isBeingRevised: boolean,
	isClientView: boolean,
	disabled: boolean,
): UseDraftRevisionsResult => {
	const [isQuotesLoading, setIsLoading] = useState(true);
	const [draftRevisions, setDraftRevisions] = useState<
		Record<number, QuoteRevisionSnapshot>
	>({});
	const [refreshingQuoteId, setRefreshingQuoteId] = useState<number | null>(null);
	const [quoteDiffKeys, setQuoteDiffKeys] = useState<Record<number, Set<string>>>(
		{},
	);

	const fetchDraftRevisions = useCallback(async () => {
		if (!isBeingRevised || !rfxInviteId || !listQuotes?.length) {
			setDraftRevisions({});
			setIsLoading(false);
			return;
		}
		try {
			const allRevisions = await getAllQuoteRevisions(rfxInviteId);
			const results: Record<number, QuoteRevisionSnapshot> = {};
			for (const quote of listQuotes) {
				const revisions = allRevisions[quote.id] ?? [];
				const drafts = revisions.filter((r) => !r.is_submitted && r.revision > 0);
				const draft = drafts.length
					? drafts.reduce((a, b) => (a.revision > b.revision ? a : b))
					: undefined;
				if (draft) {
					results[quote.id] = draft;
				}
			}
			setDraftRevisions(results);
		} catch {
			setDraftRevisions({});
		} finally {
			setIsLoading(false);
			setRefreshingQuoteId(null);
		}
	}, [isBeingRevised, rfxInviteId, listQuotes]);

	useEffect(() => {
		setIsLoading(true);
		fetchDraftRevisions();
	}, [fetchDraftRevisions]);

	useEffect(() => {
		if (!disabled || !rfxInviteId || !listQuotes?.length) {
			return;
		}

		const quotesWithRevisions = listQuotes.filter(
			(q) => (q.has_revisions && q.current_revision > 0) || draftRevisions[q.id],
		);
		if (quotesWithRevisions.length === 0) {
			return;
		}

		const fetchDiffs = async () => {
			try {
				const allRevisions = await getAllQuoteRevisions(rfxInviteId);
				const diffs: Record<number, Set<string>> = {};
				for (const quote of quotesWithRevisions) {
					const revisions = allRevisions[quote.id] ?? [];
					const sorted = [...revisions].sort((a, b) => b.revision - a.revision);
					const current = isClientView
						? sorted.find((r) => r.is_submitted)
						: sorted[0];
					const previous = sorted.find(
						(r) => r.is_submitted && r.revision < (current?.revision ?? 0),
					);
					if (current && previous) {
						if (current.cells?.length || previous.cells?.length) {
							const base = getCellDiff(
								cellDataToLookup(current.cells ?? []),
								cellDataToLookup(previous.cells ?? []),
							);
							// `total` is computed (not stored), so cell-level
							// diffing can't flag it directly. Propagate diffs
							// from input columns (quantity / response_value /
							// saving) onto the total column so its highlight
							// fires whenever the derived value changes.
							const totalCol = quote.columns?.find(
								(c) => c.columnType === "total",
							);
							if (totalCol) {
								const inputColIds = new Set(
									(quote.columns ?? [])
										.filter(
											(c) =>
												c.columnType === "response_quantity" ||
												c.columnType === "response_value" ||
												c.columnType === "saving",
										)
										.map((c) => c.id),
								);
								const augmented = new Set(base);
								for (const key of base) {
									const m = key.match(/^cell-(\d+)-(\d+)$/);
									if (!m) continue;
									const rowId = Number(m[1]);
									const colId = Number(m[2]);
									if (inputColIds.has(colId)) {
										augmented.add(`cell-${rowId}-${totalCol.id}`);
									}
								}
								diffs[quote.id] = augmented;
							} else {
								diffs[quote.id] = base;
							}
						} else {
							diffs[quote.id] = getQuoteDiff(current, previous);
						}
					}
				}
				setQuoteDiffKeys(diffs);
			} catch {
				// Error handled by global interceptor
			}
		};

		fetchDiffs();
	}, [disabled, rfxInviteId, listQuotes, isClientView, draftRevisions]);

	const hasDrafts = Object.keys(draftRevisions).length > 0;

	return {
		draftRevisions,
		isQuotesLoading,
		refreshingQuoteId,
		setRefreshingQuoteId,
		quoteDiffKeys,
		fetchDraftRevisions,
		hasDrafts,
	};
};
